import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Clock, MapPin, Phone, Star } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import { BRAND, inr, toNumber, waLink } from './brand';
import { packageImage, packageFallback, onImgError } from './packageImages';
import { isInternational, taxLabel } from './taxes';

/** Standard public page shell: fixed header, page body, footer. */
export const SitePage = ({ children, className = '' }) => (
  <>
    <Header />
    <main className={`vk-page ${className}`}>{children}</main>
    <Footer />
  </>
);

/** Breadcrumbs: items = [{ label, to? }] */
export const Crumbs = ({ items, dark = false }) => (
  <nav aria-label="Breadcrumb">
    <ol className={`vk-crumbs ${dark ? 'vk-crumbs--dark' : ''}`}>
      {items.map((c, i) => (
        <li key={`${c.label}-${i}`}>{c.to && i < items.length - 1 ? <Link to={c.to}>{c.label}</Link> : <span aria-current={i === items.length - 1 ? 'page' : undefined}>{c.label}</span>}</li>
      ))}
    </ol>
  </nav>
);

/** Big photo (or plain dark) hero used by every inner page. */
export const PageHero = ({ image, fallbackImage, imageAlt = '', kicker, title, accent, lede, crumbs, children, short = false, className = '' }) => (
  <section className={`vk-phero ${short ? 'vk-phero--short' : ''} ${image ? '' : 'vk-phero--plain'} ${className}`}>
    {image ? <img className="vk-phero__img" src={image} alt={imageAlt} fetchpriority="high" onError={(e) => { const el = e.currentTarget; if (fallbackImage && el.src !== fallbackImage) el.src = fallbackImage; else el.style.visibility = 'hidden'; }} /> : null}
    {image ? <div className="vk-phero__shade" aria-hidden="true" /> : null}
    <div className="vk-wrap vk-phero__inner">
      {crumbs ? <Crumbs items={crumbs} /> : null}
      {kicker ? <p className="vk-kicker">{kicker}</p> : null}
      <h1 className="vk-display vk-phero__title">
        {title}{accent ? <> <span>{accent}</span></> : null}
      </h1>
      {lede ? <p className="vk-phero__lede">{lede}</p> : null}
      {children ? <div className="vk-phero__actions">{children}</div> : null}
    </div>
  </section>
);

/** Orange strip of 2–4 key facts under a hero. items = [{ value, label }] */
export const Facts = ({ items }) => (
  <section className="vk-facts" aria-label="Key facts">
    <div className={`vk-wrap vk-facts__grid vk-facts__grid--${Math.min(items.length, 4)}`}>
      {items.map((f) => (
        <div key={f.label} className={`vk-facts__item ${String(f.value).length > 16 ? 'is-long' : ''}`}><strong>{f.value}</strong><span>{f.label}</span></div>
      ))}
    </div>
  </section>
);

export const SectionHead = ({ kicker, title, children, light = false }) => (
  <header className={`vk-head ${light ? 'vk-head--light' : ''}`}>
    <div>
      {kicker ? <p className="vk-kicker">{kicker}</p> : null}
      <h2 className="vk-display vk-h2">{title}</h2>
    </div>
    {children}
  </header>
);

/** Package card shared by home, destination, theme and listing pages. */
export const PackageCard = ({ pkg, onOpen, placeLabel }) => {
  const price = toNumber(pkg.price);
  const original = toNumber(pkg.originalPrice ?? pkg.original_price);
  const off = price && original && original > price ? Math.round(((original - price) / original) * 100) : null;
  const best = pkg.bestSeller === true || pkg.best_seller === 'true' || pkg.best_seller === true;
  const place = placeLabel || pkg.destination;
  // Real packages are plain links to their canonical page (crawlable, open-in-new-tab works);
  // quote-only cards (no detail page) stay buttons and use onOpen.
  const to = !pkg._pilgrimage && pkg.id && pkg.destination ? `/destination/${String(pkg.destination).toLowerCase()}/package/${pkg.id}` : null;
  const Tag = to ? Link : 'button';
  const tagProps = to ? { to } : { type: 'button', onClick: () => onOpen && onOpen(pkg) };
  return (
    <Tag {...tagProps} className="vk-card" data-testid="package-card">
      <div className="vk-card__media">
        {packageImage(pkg) ? <img src={packageImage(pkg)} alt={pkg.title || ''} loading="lazy" onError={onImgError(packageFallback(pkg))} /> : null}
        {best ? <span className="vk-card__off">Best seller</span> : null}
        {place ? <span className="vk-card__place"><MapPin size={14} /> {place}</span> : null}
      </div>
      <div className="vk-card__body">
        <h3 className="vk-card__title">{pkg.title}</h3>
        <div className="vk-card__meta">
          {pkg.duration ? <span><Clock size={14} /> {pkg.duration}</span> : null}
          {pkg.hotelClass ? <span title="Hotel category">🏨 {pkg.hotelClass} hotels</span> : null}
        </div>
        <div className="vk-card__price">
          <div>
                        <strong>{price ? inr(price) : 'Price on request'}</strong>
            {price ? <small>per person {taxLabel(isInternational(pkg))}</small> : null}
          </div>
          <span className="vk-card__go" aria-hidden="true"><ArrowUpRight size={20} /></span>
        </div>
      </div>
    </Tag>
  );
};

export const CardSkeletons = ({ count = 4 }) => (
  <div className="vk-grid">{Array.from({ length: count }).map((_, i) => <div key={i} className="vk-card vk-card--skeleton" />)}</div>
);

/** Yellow closing call-to-action band with office locations. */
export const CtaBand = ({ title = 'Tell us your dates.', accent = "We'll handle the rest.", message }) => (
  <section className="vk-cta">
    <div className="vk-wrap">
      <h2 className="vk-display vk-cta__title">{title}<br /><span>{accent}</span></h2>
      <div className="vk-cta__actions">
        <a className="vk-btn vk-btn--dark" href={waLink(message)} target="_blank" rel="noopener noreferrer">WhatsApp for a quote <ArrowRight size={18} /></a>
        <a className="vk-btn vk-btn--outline" href={BRAND.phoneLink}><Phone size={18} /> {BRAND.phone}</a>
      </div>
      <p className="vk-cta__offices">{BRAND.offices}</p>
    </div>
  </section>
);

/** Full-page loading state that keeps header/footer in place. */
export const PageLoading = () => (
  <SitePage>
    <div className="vk-wrap" style={{ padding: '96px 20px' }}>
      <div className="vk-card--skeleton" style={{ height: 320, borderRadius: 18 }} />
      <div style={{ height: 24 }} />
      <CardSkeletons count={4} />
    </div>
  </SitePage>
);
